/**
 * The single fetch wrapper. Every call is `no-store` because a stale leaderboard is a
 * feature failure, and every write carries the CSRF token the server pairs with the session
 * cookie — no exceptions, so a new route cannot forget it.
 */

import type {
  Account,
  AuditEntry,
  Division,
  EntryView,
  League,
  LeagueDetail,
  LeagueImage,
  Member,
  MemberRole,
  Mutation,
  Profile,
  Round,
  RoundView,
  Roster,
  ScoreResult,
  ScoreWrite,
  Shooter,
  ShooterStatus,
  SignupMode,
  Standings,
  TargetImage,
  TargetTemplate,
  User,
} from "./types";

const BASE = "/api/v1";

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.status = status;
    this.detail = detail;
  }
}

type Query = Record<string, string | number | undefined | null>;

function qs(query?: Query): string {
  if (!query) return "";
  const pairs = Object.entries(query).filter(
    (entry): entry is [string, string | number] => entry[1] !== undefined && entry[1] !== null,
  );
  return pairs.length === 0 ? "" : `?${new URLSearchParams(pairs.map(([k, v]) => [k, String(v)]))}`;
}

let csrfToken: string | null = null;

/** The sentence the middleware answers with when the double-submit halves disagree. */
const CSRF_REFUSAL = "Security token missing or expired";

type Payload = { detail?: unknown } | null;

/**
 * What the app understood when a call did not work. A server that answers with JSON says so in
 * `detail`; anything else — a reverse proxy's HTML error page, a captive portal, an extension
 * that refuses the request on the way out — has to be described instead of silently reported as
 * `null`. Naming the call and quoting the answer is the difference between "Request failed (500)"
 * and a report that says which request broke and who actually answered it.
 */
type Attempt = { status: number; payload: Payload; foreign: string | null };

/** First line of a non-JSON body, stripped of tags and shortened to something a card can show. */
function summarise(body: string): string | null {
  const line = body.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (!line) return null;
  return line.length > 140 ? `${line.slice(0, 140)}…` : line;
}

function describeFailure(status: number, method: string, path: string, foreign: string | null) {
  const who = foreign ? ` — answered by something other than Aimly: ${foreign}` : "";
  return `${method} ${BASE}${path} failed (${status})${who}`;
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown | FormData; query?: Query } = {},
): Promise<T> {
  const { method = "GET", body, query } = options;
  const unsafe = method !== "GET" && method !== "HEAD";

  const attempt = async (): Promise<Attempt> => {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (unsafe) {
      if (csrfToken === null) {
        // A write before any GET means a deep link straight into the entry screen; the
        // server will refuse it without the token, so fetch one rather than failing.
        await refreshCsrf();
      }
      headers["X-CSRF-Token"] = csrfToken ?? "";
      // Only JSON requests declare a content type. A FormData body must keep the boundary
      // fetch generates for it — a hand-set `application/json` here is how an upload
      // arrives as the string "{}" and every field validation trips.
      if (!(body instanceof FormData)) headers["Content-Type"] = "application/json";
    }
    const response = await fetch(`${BASE}${path}${qs(query)}`, {
      method,
      headers,
      credentials: "same-origin",
      cache: "no-store",
      body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
    });
    if (response.status === 204) return { status: 204, payload: null, foreign: null };
    const text = await response.text().catch(() => "");
    try {
      return { status: response.status, payload: JSON.parse(text) as Payload, foreign: null };
    } catch {
      return { status: response.status, payload: null, foreign: summarise(text) };
    }
  };

  let { status, payload, foreign } = await attempt();
  // The token belongs to the browser, not to this tab, so it can go stale under us: a second
  // tab asked for its own, or the server restarted with a different signing key. One re-fetch
  // and one repeat keeps that invisible without charging every write an extra round trip.
  if (unsafe && status === 403 && payload?.detail === CSRF_REFUSAL) {
    await refreshCsrf();
    ({ status, payload, foreign } = await attempt());
  }

  if (status === 204) return undefined as T;

  if (status < 200 || status > 299) {
    const detail =
      typeof payload?.detail === "string" ? payload.detail : describeFailure(status, method, path, foreign);
    throw new ApiError(status, detail);
  }

  return payload as T;
}

async function refreshCsrf(): Promise<string> {
  const { token } = await request<{ token: string }>("/auth/csrf");
  csrfToken = token;
  return token;
}

export const api = {
  // --- session -------------------------------------------------------------------
  me: () => request<{ user: User | null }>("/auth/me"),
  csrf: refreshCsrf,
  login: (username: string, password: string) =>
    request<{ user: User; must_change_password: boolean }>("/auth/login", {
      method: "POST",
      body: { username, password },
    }),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),
  changePassword: (current_password: string, new_password: string) =>
    request<{ user: User }>("/auth/password", {
      method: "POST",
      body: { current_password, new_password },
    }),

  // --- site admin: accounts --------------------------------------------------------
  // The create and reset answers may carry a `warning` — the CLI's short-password note —
  // shaped exactly like `patchRound`'s.
  accounts: () => request<Account[]>("/accounts"),
  createAccount: (payload: {
    username: string;
    display_name?: string;
    password: string;
    is_site_admin?: boolean;
    must_change_password?: boolean;
  }) => request<Account & { warning?: string }>("/accounts", { method: "POST", body: payload }),
  patchAccount: (
    user_id: string,
    payload: {
      display_name?: string;
      is_site_admin?: boolean;
      is_active?: boolean;
      must_change_password?: boolean;
    },
  ) => request<Account>(`/accounts/${user_id}`, { method: "PATCH", body: payload }),
  resetAccountPassword: (
    user_id: string,
    payload: { password: string; must_change_password?: boolean },
  ) =>
    request<Account & { warning?: string }>(`/accounts/${user_id}/password`, {
      method: "POST",
      body: payload,
    }),

  // --- public reads --------------------------------------------------------------
  leagues: () => request<League[]>("/leagues"),
  league: (slug: string) => request<LeagueDetail>(`/leagues/${encodeURIComponent(slug)}`),
  standings: (slug: string, division_id?: string) =>
    request<Standings>(`/leagues/${encodeURIComponent(slug)}/standings`, {
      query: { division_id },
    }),
  round: (slug: string, round_id: string, division_id?: string) =>
    request<RoundView>(`/leagues/${encodeURIComponent(slug)}/rounds/${encodeURIComponent(round_id)}`, {
      query: { division_id },
    }),
  profile: (slug: string, shooter_id: string) =>
    request<Profile>(`/leagues/${encodeURIComponent(slug)}/shooters/${encodeURIComponent(shooter_id)}`),
  signup: (
    slug: string,
    payload: { given_name: string; family_name: string; division_id?: string; token?: string },
  ) => request<{ ok: true; status: string }>(`/leagues/${encodeURIComponent(slug)}/signup`, {
    method: "POST",
    body: payload,
  }),

  // --- member reads --------------------------------------------------------------
  roster: (league_id: string, status: ShooterStatus | "all" = "all") =>
    request<Roster>(`/leagues/${league_id}/roster`, { query: { status } }),
  members: (league_id: string) => request<Member[]>(`/leagues/${league_id}/members`),
  audit: (league_id: string, query?: Query) =>
    request<AuditEntry[]>(`/leagues/${league_id}/audit`, { query: { limit: 100, ...query } }),
  templates: (league_id: string) =>
    request<TargetTemplate[]>(`/leagues/${league_id}/target-templates`),
  entry: (league_id: string, round_id: string, shooter_id?: string) =>
    request<EntryView>(`/leagues/${league_id}/rounds/${round_id}/entry`, {
      query: { shooter_id },
    }),

  // --- league composition --------------------------------------------------------
  createLeague: (payload: {
    name: string;
    kind: "league" | "tournament";
    summary?: string;
    description?: string;
    timezone?: string;
    score_scale?: number;
    signup_mode?: SignupMode;
    label_template?: string;
  }) => request<League>("/leagues", { method: "POST", body: payload }),
  patchLeague: (league_id: string, payload: Record<string, unknown>) =>
    request<League>(`/leagues/${league_id}`, { method: "PATCH", body: payload }),

  createDivision: (league_id: string, payload: { name: string; code?: string; sort_order?: number }) =>
    request<Division>(`/leagues/${league_id}/divisions`, { method: "POST", body: payload }),
  patchDivision: (league_id: string, division_id: string, payload: Record<string, unknown>) =>
    request<Division>(`/leagues/${league_id}/divisions/${division_id}`, {
      method: "PATCH",
      body: payload,
    }),
  deleteDivision: (league_id: string, division_id: string) =>
    request<Mutation>(`/leagues/${league_id}/divisions/${division_id}`, { method: "DELETE" }),

  createTemplate: (
    league_id: string,
    payload: { name: string; mode: "points" | "hit_miss"; shots?: number; max_value_scaled: number },
  ) => request<TargetTemplate>(`/leagues/${league_id}/target-templates`, { method: "POST", body: payload }),
  patchTemplate: (league_id: string, template_id: string, payload: Record<string, unknown>) =>
    request<TargetTemplate>(`/leagues/${league_id}/target-templates/${template_id}`, {
      method: "PATCH",
      body: payload,
    }),
  deleteTemplate: (league_id: string, template_id: string) =>
    request<Mutation>(`/leagues/${league_id}/target-templates/${template_id}`, { method: "DELETE" }),

  createRound: (
    league_id: string,
    payload: { label: string; plays_on?: string; target_ids: string[]; status?: string },
  ) => request<Round>(`/leagues/${league_id}/rounds`, { method: "POST", body: payload }),
  patchRound: (
    league_id: string,
    round_id: string,
    payload: { label?: string; plays_on?: string | null; status?: string; target_ids?: string[] },
  ) => request<Round & { warning?: string }>(`/leagues/${league_id}/rounds/${round_id}`, {
    method: "PATCH",
    body: payload,
  }),
  deleteRound: (league_id: string, round_id: string) =>
    request<Mutation>(`/leagues/${league_id}/rounds/${round_id}`, { method: "DELETE" }),

  // --- roster --------------------------------------------------------------------
  createShooter: (
    league_id: string,
    payload: { given_name: string; family_name: string; division_id?: string; is_public?: boolean },
  ) => request<Shooter>(`/leagues/${league_id}/roster`, { method: "POST", body: payload }),
  decideShooter: (league_id: string, shooter_id: string, decision: "approve" | "decline") =>
    request<Shooter>(`/leagues/${league_id}/roster/${shooter_id}/decision`, {
      method: "POST",
      body: { decision },
    }),
  patchShooter: (league_id: string, shooter_id: string, payload: Record<string, unknown>) =>
    request<Shooter>(`/leagues/${league_id}/roster/${shooter_id}`, { method: "PATCH", body: payload }),
  retireShooter: (league_id: string, shooter_id: string) =>
    request<Shooter>(`/leagues/${league_id}/roster/${shooter_id}/retire`, { method: "POST", body: {} }),
  deleteShooter: (league_id: string, shooter_id: string) =>
    request<Mutation>(`/leagues/${league_id}/roster/${shooter_id}`, { method: "DELETE" }),

  // --- membership ----------------------------------------------------------------
  addMember: (league_id: string, payload: { username: string; role: MemberRole }) =>
    request<Member>(`/leagues/${league_id}/members`, { method: "POST", body: payload }),
  removeMember: (league_id: string, user_id: string, role: MemberRole) =>
    request<Mutation>(`/leagues/${league_id}/members/${user_id}`, {
      method: "DELETE",
      query: { role },
    }),

  // --- scores --------------------------------------------------------------------
  putScore: (payload: ScoreWrite) => request<ScoreResult>("/scores", { method: "PUT", body: payload }),
  clearScore: (params: {
    league_id: string;
    round_id: string;
    shooter_id: string;
    target_id: string;
  }) => request<Mutation>("/scores", { method: "DELETE", query: { ...params } }),
  mark: (league_id: string, round_id: string, payload: { shooter_id: string; status: "dq"; note?: string }) =>
    request<Mutation>(`/leagues/${league_id}/rounds/${round_id}/marks`, { method: "POST", body: payload }),
  unmark: (league_id: string, round_id: string, shooter_id: string) =>
    request<Mutation>(`/leagues/${league_id}/rounds/${round_id}/marks/${shooter_id}`, {
      method: "DELETE",
    }),

  // --- images --------------------------------------------------------------------
  // Uploads post `FormData`; the file and thumb routes are plain URLs straight into
  // `<img src>` — the row lookup behind them does the access check the <img> tag cannot.
  images: (params: {
    league_id: string;
    round_id: string;
    shooter_id: string;
    target_id?: string;
  }) => request<{ images: TargetImage[] }>("/images", { query: { ...params } }),
  uploadImage: (form: FormData) =>
    request<{ image: TargetImage; count: number }>("/images", { method: "POST", body: form }),
  promoteImage: (image_id: string) =>
    request<{ ok: true; image: TargetImage }>(`/images/${image_id}/primary`, { method: "POST" }),
  leagueImages: (league_id: string) =>
    request<{ images: LeagueImage[] }>(`/leagues/${league_id}/images`),
  uploadLeagueImage: (league_id: string, form: FormData) =>
    request<{ image: LeagueImage }>(`/leagues/${league_id}/images`, {
      method: "POST",
      body: form,
    }),
  patchLeagueImage: (
    league_id: string,
    image_id: string,
    payload: { caption?: string; sort_order?: number },
  ) =>
    request<{ image: LeagueImage }>(`/leagues/${league_id}/images/${image_id}`, {
      method: "PATCH",
      body: payload,
    }),
  deleteLeagueImage: (league_id: string, image_id: string) =>
    request<Mutation>(`/leagues/${league_id}/images/${image_id}`, { method: "DELETE" }),
};

/** Exact integer arithmetic: a displayed value is a scaled integer, never a float. */
export function formatScaled(value: number, scale: number): string {
  const sign = value < 0 ? "-" : "";
  const magnitude = Math.abs(Math.trunc(value));
  if (scale <= 1) return `${sign}${magnitude}`;
  const unit = Math.trunc(scale);
  const whole = Math.floor(magnitude / unit);
  const fraction = String(magnitude % unit).padEnd(String(unit).length - 1, "0");
  return `${sign}${whole}.${fraction}`;
}

/**
 * Decimal text to scaled integer, without a float in the path. Returns null for anything
 * that is not a plain non-negative decimal, and throws for a negative so the caller cannot
 * confuse "invalid" with "zero" — an empty field and a typed 0 are different inputs.
 */
export function parseScaled(text: string, scale: number): number | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const match = /^(\d+)(?:\.(\d+))?$/.exec(trimmed);
  if (!match) return null;
  const [, whole, fraction = ""] = match;
  const digits = String(Math.trunc(scale)).length - 1;
  if (fraction.length > digits) return null;
  const scaled = BigInt(whole) * BigInt(Math.trunc(scale)) + BigInt(fraction.padEnd(digits, "0"));
  if (scaled > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  return Number(scaled);
}

export function maxFractionDigits(scale: number): number {
  return String(Math.trunc(scale)).length - 1;
}
